import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  ArrowLeft,
  Calendar,
  Diamond,
  FileText,
  Users,
  TrendingUp,
  CreditCard,
  Award,
  CheckCircle,
  AlertCircle,
  Gem,
  Hash,
  ShoppingBasket
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { multiNivelService, Receipt } from '@/services/multi-nivel.service';

const ExtratoMensal: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState<any>(null);

  useEffect(() => {
    loadReceipts();
  }, []);

  const loadReceipts = async () => {
    const userDataString = localStorage.getItem('userlogged');
    let parsedUserData;
    if (userDataString) {
      parsedUserData = JSON.parse(userDataString);
      setUserData(parsedUserData);
    }

    setLoading(true);
    try {
      const data = await multiNivelService.getReceiptsMesAtualByUserId(parsedUserData.id);
      setReceipts(data);
    } catch (error) {
      console.error('Erro ao carregar receipts:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar o extrato mensal.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrentMonth = () => {
    const now = new Date();
    return now.toLocaleDateString('pt-BR', { 
      month: 'long', 
      year: 'numeric' 
    }).charAt(0).toUpperCase() + now.toLocaleDateString('pt-BR', { 
      month: 'long', 
      year: 'numeric' 
    }).slice(1);
  };

  const getTipoLabel = (tipo: string) => {
    switch (tipo) {
      case 'venda':
        return 'Diamantes Indicação - Venda';
      case 'aprovacao':
        return 'Bônus por Aprovação';
      case 'primeira_venda':
        return 'Bônus de Ativação';
      default:
        return tipo;
    }
  };

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case 'venda':
        return <ShoppingBasket className="w-5 h-5 text-blue-600" />;
      case 'aprovacao':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'primeira_venda':
        return <Award className="w-5 h-5 text-purple-600" />;
      default:
        return <Diamond className="w-5 h-5 text-gray-600" />;
    }
  };

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case 'venda':
        return 'bg-blue-50 border-blue-200';
      case 'aprovacao':
        return 'bg-green-50 border-green-200';
      case 'primeira_venda':
        return 'bg-purple-50 border-purple-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getNivelBadgeColor = (nivel: number) => {
    switch (nivel) {
      case 1:
        return 'bg-blue-100 text-blue-800';
      case 2:
        return 'bg-green-100 text-green-800';
      case 3:
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const totalDiamantes = receipts.reduce((total, receipt) => total + receipt.diamantes_gerados, 0);
  const totalReceiptsVenda = receipts.filter(r => r.tipo === 'venda').length;
  const totalReceiptsAprovacao = receipts.filter(r => r.tipo === 'aprovacao').length;
  const totalReceiptsPrimeiraVenda = receipts.filter(r => r.tipo === 'primeira_venda').length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background pb-12">
      {/* Header */}
      <div className="sticky top-0 bg-white/80 backdrop-blur-sm border-b border-gray-200 z-10">
        <div className="px-4 py-4 flex items-center gap-3">
          <button 
            onClick={() => navigate('/minha-rede')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Extrato Mensal</h1>
            <p className="text-sm text-gray-500">{formatCurrentMonth()}</p>
          </div>
        </div>
      </div>

      <div className="px-4 pt-6 space-y-6">
        {/* Cards de Resumo */}
        <div className="grid grid-cols-2 gap-4">
          {/* Total de Diamantes */}
          <Card className="bg-gradient-to-br from-purple-50 to-purple-300 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-200 rounded-lg">
                  <Gem className="w-5 h-5 text-purple-700" />
                </div>
                <div>
                  <p className="text-xs text-purple-700 font-medium">Total Diamantes</p>
                  <p className="text-lg font-bold text-purple-900">{totalDiamantes.toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total de Receipts */}
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-200 rounded-lg">
                  <FileText className="w-5 h-5 text-blue-700" />
                </div>
                <div>
                  <p className="text-xs text-blue-700 font-medium">Extratos </p>
                  <p className="text-lg font-bold text-blue-900">{receipts.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Breakdown por Tipo */}
        {/* <div className="grid grid-cols-3 gap-3">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-3 text-center">
              <CreditCard className="w-6 h-6 text-blue-600 mx-auto mb-1" />
              <p className="text-xs text-blue-700 font-medium">Vendas</p>
              <p className="text-lg font-bold text-blue-900">{totalReceiptsVenda}</p>
            </CardContent>
          </Card>

          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-3 text-center">
              <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-1" />
              <p className="text-xs text-green-700 font-medium">Aprovações</p>
              <p className="text-lg font-bold text-green-900">{totalReceiptsAprovacao}</p>
            </CardContent>
          </Card>

          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-3 text-center">
              <Award className="w-6 h-6 text-purple-600 mx-auto mb-1" />
              <p className="text-xs text-purple-700 font-medium">Primeiras</p>
              <p className="text-lg font-bold text-purple-900">{totalReceiptsPrimeiraVenda}</p>
            </CardContent>
          </Card>
        </div> */}

        {/* Lista de Receipts */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Histórico
          </h2>

          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-20 bg-gray-200 rounded-lg"></div>
                </div>
              ))}
            </div>
          ) : receipts.length > 0 ? (
            <div className="space-y-3">
              {receipts.map((receipt, index) => (
                <Card key={index} className={`${getTipoColor(receipt.tipo)} hover:shadow-md transition-shadow`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      {/* Info principal */}
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-white rounded-lg shadow-sm">
                          {getTipoIcon(receipt.tipo)}
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-gray-900 text-sm">
                              {getTipoLabel(receipt.tipo)}
                            </p>
                            <Badge className={`text-xs px-2 py-0.5 ${getNivelBadgeColor(receipt.nivel)}`}>
                              Nível {receipt.nivel}
                            </Badge>
                          </div>
                          
                          <div className="space-y-1">
                            <p className="text-xs text-gray-600">
                              <Calendar className="w-3 h-3 inline mr-1" />
                              {formatDate(receipt.created_at)}
                            </p>
                            
                            {receipt.codigo_pagarme && (
                              <p className="text-xs text-gray-600">
                                <Hash className="w-3 h-3 inline mr-1" />
                                {receipt.codigo_pagarme}
                              </p>
                            )}
                            
                            <p className="text-xs text-gray-600">
                              <Users className="w-3 h-3 inline mr-1" />
                              Indicação #{receipt.codigo_vendedora}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Valor dos diamantes */}
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900">
                          {receipt.diamantes_gerados.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-600">diamantes</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Nenhum extrato encontrado para este mês. As comissões aparecem aqui quando você recebe diamantes por indicações.
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Informações sobre receipts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <TrendingUp className="w-4 h-4" />
              Sobre os Extratos
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <p>
              • <strong>Diamantes Indicação:</strong> Diamantes recorrentes que você recebe sempre que suas indicadas (clientes ou revendedoras) realizam compras ou vendas pelo app. O percentual varia conforme o nível da rede (1º, 2º ou 3º).
            </p>          
            <p>
              • <strong>Bônus Ativação de Cliente:</strong> você recebe 15 diamantes extras quando a sua indicação direta de cliente realiza a primeira compra de pelo menos R$100,00. Esse bônus é concedido apenas uma vez por cliente indicado direto pelo seu link ou código.
            </p>           
          </CardContent>
        </Card>
      </div>

      {/* Espaçamento para navegação mobile */}
      <div className="h-20 lg:h-0"></div>
    </div>
  );
};

export default ExtratoMensal;
